import { calculateRms } from "@jugl-web/utils/audio/calculateRms";
import { type FC, useEffect, useMemo, useRef } from "react";

interface WaveformAnimationProps {
  data: Float32Array<ArrayBuffer> | undefined | null;
  isActive: boolean;
  numberOfBars: number;
}

export const WaveformAnimation: FC<WaveformAnimationProps> = ({
  data,
  isActive,
  numberOfBars,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const rafIdRef = useRef<number | null>(null);

  const emptyBars = useMemo(
    () => new Float32Array(numberOfBars),
    [numberOfBars]
  );

  useEffect(() => {
    if (!canvasRef.current) {
      return undefined;
    }

    const canvas = canvasRef.current;
    const canvasCtx = canvas.getContext("2d") as CanvasRenderingContext2D;
    const canvasSize = resizeCanvas(canvas, canvasCtx);

    const bars = new Float32Array(numberOfBars);

    if (!data) {
      drawBars(canvasCtx, canvasSize, isActive ? bars : emptyBars);
      return undefined;
    }

    const sampleInterval = 1000 / 30;
    const riseFactor = 0.18;
    const fallFactor = 0.06;

    let displayedLevel = 0;
    let lastPushTime = 0;

    const tick = (timestamp: number) => {
      const rms = calculateRms(data);

      const minRms = 0.05;
      const maxRms = 0.1;

      const targetLevel = Math.max(
        0,
        Math.min(1, (rms - minRms) / (maxRms - minRms))
      );

      const smoothingFactor =
        targetLevel > displayedLevel ? riseFactor : fallFactor;

      displayedLevel += (targetLevel - displayedLevel) * smoothingFactor;

      if (timestamp - lastPushTime >= sampleInterval) {
        pushValue(bars, displayedLevel);
        lastPushTime = timestamp;
      }

      drawBars(canvasCtx, canvasSize, isActive ? bars : emptyBars);

      rafIdRef.current = requestAnimationFrame(tick);
    };

    rafIdRef.current = requestAnimationFrame(tick);

    return () => {
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = null;
      }
    };
  }, [data, emptyBars, isActive, numberOfBars]);

  return (
    <canvas
      ref={canvasRef}
      style={{
        width: 92,
        height: 26,
      }}
    />
  );
};

const pushValue = (values: Float32Array<ArrayBuffer>, nextValue: number) => {
  values.copyWithin(0, 1);
  values[values.length - 1] = nextValue;
};

const resizeCanvas = (
  canvas: HTMLCanvasElement,
  context: CanvasRenderingContext2D
): { width: number; height: number } => {
  const rect = canvas.getBoundingClientRect();
  const dpr = window.devicePixelRatio || 1;

  const targetWidth = Math.round(rect.width * dpr);
  const targetHeight = Math.round(rect.height * dpr);

  if (canvas.width !== targetWidth || canvas.height !== targetHeight) {
    canvas.width = targetWidth;
    canvas.height = targetHeight;
    context.setTransform(dpr, 0, 0, dpr, 0, 0);
  }

  return {
    width: rect.width,
    height: rect.height,
  };
};

const drawBars = (
  context: CanvasRenderingContext2D,
  size: { width: number; height: number },
  values: Float32Array
): void => {
  const { width, height } = size;

  context.clearRect(0, 0, width, height);

  const barCount = values.length;

  const barWidth = 2;
  const gap = 3;

  const totalWidth = barCount * barWidth + (barCount - 1) * gap;

  const startX = (width - totalWidth) / 2;
  const centerY = height / 2;

  const minBarHeight = 10;
  const maxBarHeight = 26;

  context.fillStyle = "#1A2023";

  for (let index = 0; index < barCount; index += 1) {
    const normalizedValue = Math.max(0, Math.min(values[index] ?? 0, 1));

    const barHeight =
      minBarHeight + normalizedValue * (maxBarHeight - minBarHeight);

    const x = startX + index * (barWidth + gap);
    const y = centerY - barHeight / 2;

    context.beginPath();
    context.roundRect(x, y, barWidth, barHeight, barWidth / 2);
    context.fill();
  }
};
